import { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  useTheme,
  Tooltip,
  Chip,
  CircularProgress,
  Alert,
} from '@mui/material';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { useSelector } from 'react-redux';
import { useServices } from '../../hooks/service/useServices';
import { useServiceActions } from '../../hooks/service/useServiceActions';
import ServiceDrawer from '../../components/services/ServiceDrawer';
import { Service } from '../../types/service';
import ConfirmationDialog from '../../components/common/ConfirmationDialog';
import Notifications from '../../components/common/Notifications';
import { useAuth } from '../../hooks';
import { AUTH_CONSTANTS } from '../../config/constants';
import { NotificationT, SEVERITY } from '../../types/notification';
import EmptyStateIcon from '../../assets/images/emptyState-illus.svg';
import { selectPaymentSettings } from '../../store/slices/paymentSlice';
import { formatCents } from '../../utils/formatters';

const ServicesView = ({ isActivation = false }: { isActivation?: boolean }) => {
  const theme = useTheme();
  const { services, isLoading, error, selectedOrganizationId, refetch } = useServices();
  const { deleteService } = useServiceActions();
  const { role } = useAuth();
  const paymentSettings = useSelector(selectPaymentSettings);

  // Check if Stripe is connected and charges are enabled (hide during activation)
  const isStripeConnected =
    !isActivation &&
    paymentSettings?.onboardingStatus === 'COMPLETE' &&
    paymentSettings?.chargesEnabled;

  // UI State
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedService, setSelectedService] = useState<Service | null>(null);

  // Confirmation Dialog State
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    serviceId: 0,
  });

  // Notification State
  const [notification, setNotification] = useState<NotificationT>({
    open: false,
    message: '',
    severity: SEVERITY.SUCCESS,
  });

  if (!selectedOrganizationId) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">Please select an organization to view its services.</Alert>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  const handleEdit = (service: Service) => {
    setSelectedService(service);
    setIsDrawerOpen(true);
  };

  const handleDeleteClick = (serviceId: number) => {
    setConfirmDialog({
      open: true,
      serviceId,
    });
  };

  const handleDeleteConfirm = async () => {
    try {
      await deleteService(confirmDialog.serviceId);
      await refetch();
      setNotification({
        open: true,
        message: 'Service deleted successfully',
        severity: SEVERITY.SUCCESS,
      });
    } catch (error) {
      setNotification({
        open: true,
        message: 'Failed to delete service',
        severity: SEVERITY.ERROR,
      });
    } finally {
      setConfirmDialog({ open: false, serviceId: 0 });
    }
  };

  const handleDrawerClose = () => {
    setIsDrawerOpen(false);
    setSelectedService(null);
    refetch();
  };

  const handleServiceUpdate = async () => {
    await refetch();

    // If a service is currently selected, update it with the latest data
    if (selectedService) {
      const updatedService = services.find(s => s.id === selectedService.id);
      if (updatedService) {
        setSelectedService(updatedService);
      }
    }
  };

  const currentService = selectedService
    ? services.find(s => s.id === selectedService.id) || selectedService
    : null;

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', sm: 'center' },
          gap: { xs: 2, sm: 0 },
          mb: 3,
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 500 }}>
          Services
        </Typography>

        {role !== AUTH_CONSTANTS.ROLES.USER && services.length ? (
          <Button
            variant="contained"
            startIcon={<Plus size={20} />}
            onClick={() => setIsDrawerOpen(true)}
            className="services-tour"
            sx={{
              borderRadius: '12px',
              fontWeight: 600,
              textTransform: 'none',
              px: 3,
              py: 1.5,
              boxShadow: '0 4px 14px 0 rgba(43, 23, 172, 0.39)',
              '&:hover': {
                boxShadow: '0 6px 20px 0 rgba(43, 23, 172, 0.5)',
                transform: 'translateY(-1px)',
              },
            }}
          >
            Add Service
          </Button>
        ) : null}
      </Box>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      ) : !services.length ? (
        role === AUTH_CONSTANTS.ROLES.USER ? (
          <Paper elevation={0}>
            <Box
              sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', px: 3, py: 6 }}
            >
              <img className="w-28 mx-auto" src={EmptyStateIcon} alt="No data found" />
              <Typography variant="h6" sx={{ mt: 2 }}>
                No services found
              </Typography>
            </Box>
          </Paper>
        ) : (
          <Paper elevation={0}>
            <Box
              sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', px: 3, py: 6 }}
            >
              <img className="w-28 mx-auto" src={EmptyStateIcon} alt="No data found" />
              <Typography variant="h6" sx={{ mt: 2 }}>
                No services found
              </Typography>
              <Button
                variant="contained"
                startIcon={<Plus size={20} />}
                onClick={() => setIsDrawerOpen(true)}
                sx={{
                  mt: 3,
                  borderRadius: '12px',
                  fontWeight: 600,
                  textTransform: 'none',
                  px: 3,
                  py: 1.5,
                  boxShadow: '0 4px 14px 0 rgba(43, 23, 172, 0.39)',
                  '&:hover': {
                    boxShadow: '0 6px 20px 0 rgba(43, 23, 172, 0.5)',
                    transform: 'translateY(-1px)',
                  },
                }}
                className="services-tour"
              >
                Add Service
              </Button>
            </Box>
          </Paper>
        )
      ) : (
        <Paper elevation={0}>
          <TableContainer
            sx={{
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 2,
              overflowX: 'auto',
            }}
          >
            <Table sx={{ minWidth: { xs: 500, sm: 'auto' } }}>
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{ px: { xs: 2, sm: 3 }, py: 1, color: theme.palette.text.secondary }}
                  >
                    Name & Description
                  </TableCell>
                  <TableCell
                    sx={{
                      py: 1,
                      color: theme.palette.text.secondary,
                      minWidth: { xs: 80, sm: 140 },
                      display: { xs: 'none', sm: 'table-cell' },
                    }}
                  >
                    Duration (min)
                  </TableCell>
                  {isStripeConnected && (
                    <TableCell
                      sx={{
                        py: 1,
                        color: theme.palette.text.secondary,
                        minWidth: { xs: 80, sm: 100 },
                        display: { xs: 'none', sm: 'table-cell' },
                      }}
                    >
                      Price
                    </TableCell>
                  )}
                  <TableCell
                    sx={{
                      py: 1,
                      color: theme.palette.text.secondary,
                      display: { xs: 'none', md: 'table-cell' },
                    }}
                  >
                    Resources
                  </TableCell>
                  <TableCell
                    sx={{
                      py: 1,
                      color: theme.palette.text.secondary,
                      display: { xs: 'none', md: 'table-cell' },
                    }}
                  >
                    Addons
                  </TableCell>
                  {role !== AUTH_CONSTANTS.ROLES.USER && !isActivation ? (
                    <TableCell
                      align="right"
                      sx={{
                        py: 1,
                        color: theme.palette.text.secondary,
                        minWidth: { xs: 80, sm: 120 },
                      }}
                    >
                      Actions
                    </TableCell>
                  ) : null}
                </TableRow>
              </TableHead>
              <TableBody>
                {services.map(service => (
                  <TableRow
                    key={service.id}
                    hover
                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                  >
                    <TableCell sx={{ fontSize: 16, px: { xs: 2, sm: 3 } }}>
                      <Typography
                        sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}
                      >
                        {service.name}
                        {service.isAddonService ? (
                          <Typography
                            variant="caption"
                            color="primary"
                            sx={{
                              fontWeight: 500,
                              textTransform: 'uppercase',
                              lineHeight: 1,
                              backgroundColor: `${theme.palette.primary.main}10`,
                              py: 0.25,
                              pt: 0.5,
                              px: 0.75,
                              borderRadius: 2,
                            }}
                          >
                            Addon Service
                          </Typography>
                        ) : service.isNewUserService ? (
                          <Typography
                            variant="caption"
                            color="primary"
                            sx={{
                              fontWeight: 500,
                              textTransform: 'uppercase',
                              lineHeight: 1,
                              backgroundColor: `${theme.palette.primary.main}10`,
                              py: 0.25,
                              pt: 0.5,
                              px: 0.75,
                              borderRadius: 2,
                            }}
                          >
                            New User Service
                          </Typography>
                        ) : (
                          ''
                        )}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {service.description}
                      </Typography>
                    </TableCell>

                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                      {service.duration}
                    </TableCell>
                    {isStripeConnected && (
                      <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                        {service.priceCents !== null && service.priceCents !== undefined ? (
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 500,
                              color: theme.palette.success.main,
                            }}
                          >
                            {formatCents(service.priceCents, service.currency || 'AUD')}
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            Free
                          </Typography>
                        )}
                      </TableCell>
                    )}
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {service.serviceResourceMapping.map(({ resources }) => (
                          <Chip
                            key={resources.id}
                            label={resources.name}
                            size="small"
                            sx={{
                              backgroundColor: `${theme.palette.primary.main}10`,
                              color: theme.palette.primary.main,
                            }}
                          />
                        ))}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {service.serviceAddons.map(({ serviceAddon: addons }) => (
                          <Chip
                            key={addons.id}
                            label={addons.name}
                            size="small"
                            sx={{
                              backgroundColor: `${theme.palette.primary.main}10`,
                              color: theme.palette.primary.main,
                            }}
                          />
                        ))}
                      </Box>
                    </TableCell>
                    {role !== AUTH_CONSTANTS.ROLES.USER && !isActivation ? (
                      <TableCell align="right">
                        <Tooltip title="Edit">
                          <IconButton
                            onClick={() => handleEdit(service)}
                            size="small"
                            sx={{ mr: 2 }}
                          >
                            <Pencil size={16} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            onClick={() => handleDeleteClick(service.id)}
                            size="small"
                            color="error"
                          >
                            <Trash2 size={16} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    ) : null}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      <ServiceDrawer
        open={isDrawerOpen}
        onClose={handleDrawerClose}
        service={currentService || undefined}
        refetchServices={handleServiceUpdate}
      />

      <ConfirmationDialog
        open={confirmDialog.open}
        title="Delete Service"
        message="Are you sure you want to delete this service? This action cannot be undone."
        onConfirm={handleDeleteConfirm}
        onCancel={() => setConfirmDialog({ open: false, serviceId: 0 })}
      />

      <Notifications
        open={notification.open}
        message={notification.message}
        severity={notification.severity}
        onClose={() => setNotification(prev => ({ ...prev, open: false }))}
      />
    </Box>
  );
};

export default ServicesView;
